import React, { FC, useState } from 'react';
import { connect } from 'react-redux';
import { message, Popconfirm } from 'antd';
import OWButtonWithSupplierManagedLock from '../common/OWButtonWithSupplierManagedLock';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { unlinkSubAssetForBuyer, unlinkSubAssetForSupplier } from '../../thunks/assets_thunks';
import { withUserType } from '../withUserType';

interface UnlinkSubAssetButtonProps {
	parentAssetId: number | string;
	childAssetId: number | string;
	// Called after a successful unlink so the tab can refetch its list.
	onUnlinked: () => void;
	// Injected by connect.
	userType?: string;
	unlinkSubAssets?: (parentAssetId, assetIds) => Promise<any>;
}

const UnlinkSubAssetButton: FC<UnlinkSubAssetButtonProps> = ({
	parentAssetId,
	childAssetId,
	onUnlinked,
	unlinkSubAssets,
}) => {
	const [loading, setLoading] = useState(false);

	const onConfirm = () => {
		setLoading(true);
		unlinkSubAssets(parentAssetId, [childAssetId])
			.then(() => onUnlinked())
			.catch((error) => {
				message.error(typeof error === 'string' ? error : 'Failed to unlink sub asset');
			})
			.finally(() => setLoading(false));
	};

	return (
		<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_ASSETS}>
			{/* Stop the click from bubbling to the row's onClick (which navigates to
			    the asset detail page) when opening the confirm popover. */}
			<span onClick={(event) => event.stopPropagation()}>
				<Popconfirm
					placement="topRight"
					title="Unlink this sub asset? It will no longer be a sub asset of this asset."
					okText="Unlink"
					cancelText="Cancel"
					okButtonProps={{ danger: true, loading }}
					onConfirm={onConfirm}
				>
					<OWButtonWithSupplierManagedLock type="ghost" loading={loading}>
						Unlink
					</OWButtonWithSupplierManagedLock>
				</Popconfirm>
			</span>
		</AccessPermissionChecker>
	);
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	unlinkSubAssets: (parentAssetId, assetIds) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? unlinkSubAssetForSupplier(parentAssetId, assetIds)
				: unlinkSubAssetForBuyer(parentAssetId, assetIds)
		),
});

const ConnectedUnlinkSubAssetButton = connect(null, mapDispatchToProps)(UnlinkSubAssetButton);

// withUserType feeds `userType` down as a prop so mapDispatchToProps can pick the
// role-appropriate thunk.
export default withUserType(ConnectedUnlinkSubAssetButton);
